import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CompletionCelebration from "./CompletionCelebration";
import { Logo } from "@/components/Logo";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  HeartHandshake, Video, FileText, ListChecks, MessagesSquare, ArrowRight, CalendarClock,
  UserRound, Sparkles, Map, Users, UserPlus, Target,
} from "lucide-react";

const fmt = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleString([], { dateStyle: "full", timeStyle: "short" }); } catch { return iso; } };
const LANE_LABELS = {
  "keep-mama-home": "Keep Mama Home", "move-mama-into-my-home": "Move Mama Into My Home",
  "selling-the-home": "Selling the Home", "senior-living-placement": "Senior Living Placement",
  "downsizing-estate-support": "Downsizing & Estate Support", "family-transition-roadmap": "Family Transition Roadmap",
  "start-here": "Getting Started", sell_home: "Selling the Home", keep_home: "Keep Mama Home",
  placement: "Senior Living Placement", downsize: "Downsizing & Estate Support", need_roadmap: "Family Transition Roadmap",
};
const laneLabel = (l) => LANE_LABELS[l] || (l ? l.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Being matched");

const Tile = ({ icon: Icon, title, desc, to, testid }) => {
  const nav = useNavigate();
  return (
    <button onClick={() => nav(to)} data-testid={testid}
      className="text-left w-full rounded-2xl border border-[#E6EDF2] bg-white p-5 hover:border-[#1C4358]/40 hover:shadow-sm transition-all group">
      <span className="h-11 w-11 rounded-xl bg-[#1C4358]/8 text-[#1C4358] flex items-center justify-center"><Icon size={20} /></span>
      <div className="mt-3 font-display font-semibold text-[16px] text-[#252221] flex items-center gap-1">{title} <ArrowRight size={15} className="text-[#9C1E22] opacity-0 group-hover:opacity-100 transition-opacity" /></div>
      <div className="text-[13px] text-muted-foreground mt-0.5">{desc}</div>
    </button>
  );
};

export default function ClientPortalHome() {
  const { user } = useAuth();
  const [w, setW] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const loadWelcome = () => api.get("/portal/welcome").then(({ data }) => setW(data)).catch(() => {});
  useEffect(() => {
    loadWelcome().finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="space-y-4"><Skeleton className="h-40" /><div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"><Skeleton className="h-36" /><Skeleton className="h-36" /><Skeleton className="h-36" /></div></div>;

  const name = (w?.name || user?.full_name || "there").split(" ")[0];
  const prog = w?.progress || { percent: 0, completed: 0, total: 0 };
  const inviteFamily = async () => {
    try { await api.post("/portal/family-invite-nudge"); } catch { /* tracking is best-effort */ }
    nav("/portal/profile");
  };

  return (
    <div data-testid="client-portal-home" className="space-y-6">
      {w?.celebration_pending && <CompletionCelebration onDone={loadWelcome} />}
      {/* Warm welcome hero */}
      <Card className="p-6 sm:p-8 border-0 text-white overflow-hidden relative" style={{ background: "linear-gradient(135deg,#1C4358 0%,#0f2733 100%)" }} data-testid="client-welcome-hero">
        <HeartHandshake size={120} className="absolute -right-4 -bottom-6 opacity-10" />
        <div className="relative">
          <div className="flex items-center gap-2.5 mb-4" data-testid="portal-welcome-logo">
            <Logo size={40} />
            <span className="font-display font-bold text-white text-[15px]">MovingMyMama</span>
          </div>
          <div className="inline-flex items-center gap-1.5 text-[12px] font-semibold bg-white/15 rounded-full px-3 py-1">
            <Sparkles size={13} /> {w?.first_login ? "Welcome to your family portal" : "Welcome back"}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-3">
            {w?.first_login ? <>Welcome, {name}.</> : <>Hello again, {name}.</>}
          </h1>
          <p className="text-[15px] sm:text-[16px] text-white/80 mt-2 max-w-xl">
            {w?.first_login
              ? "We saved your information so your family doesn't have to start over. Your plan, meetings, documents, and next steps are all here in one place."
              : "You don't have to figure this out alone. Your family's plan, meetings, and next steps are right here."}
          </p>
          <div className="mt-4 rounded-xl bg-white/10 p-4 flex items-start gap-3" data-testid="client-next-step">
            <span className="h-9 w-9 rounded-lg bg-[#9C1E22] flex items-center justify-center shrink-0"><ArrowRight size={18} /></span>
            <div>
              <div className="text-[12px] uppercase tracking-wide text-white/60 font-semibold">Your next step</div>
              <div className="text-[15px] font-medium">{w?.next_step}</div>
            </div>
          </div>
          {w?.upcoming_meeting && (
            <button onClick={() => nav("/portal/meetings")} data-testid="client-upcoming-meeting"
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-white text-[#1C4358] font-semibold text-[14px] h-11 px-5 hover:bg-white/90 transition-colors">
              <Video size={16} /> {w.upcoming_meeting.title} · {fmt(w.upcoming_meeting.scheduled_at)}
            </button>
          )}
        </div>
      </Card>

      {/* Roadmap progress + family invite nudge */}
      {(prog.total > 0 || w?.is_primary) && (
        <div className="grid lg:grid-cols-3 gap-4">
          {prog.total > 0 && (
            <Card className="p-5 border-[#E6EDF2] lg:col-span-2" data-testid="dashboard-roadmap-progress">
              <div className="flex items-center justify-between">
                <div className="text-[11px] uppercase tracking-wide text-[#1C4358]/60 font-bold flex items-center gap-1.5"><Target size={13} /> Roadmap progress</div>
                <div className="text-[13px] font-semibold text-[#1C4358]" data-testid="dashboard-progress-count">{prog.completed} of {prog.total} next steps completed</div>
              </div>
              <div className="mt-2 flex items-center gap-3">
                <Progress value={prog.percent} className="h-2.5 flex-1" data-testid="dashboard-progress-bar" />
                <span className="text-[15px] font-bold text-[#252221] tabular-nums" data-testid="dashboard-progress-percent">{prog.percent}%</span>
              </div>
              <div className="text-[14px] text-[#252221] mt-2.5">
                Your family transition plan is <span className="font-semibold">{prog.percent}% underway</span>.
              </div>
              {w?.next_recommended_step && (
                <div className="text-[13px] text-muted-foreground mt-1" data-testid="dashboard-next-recommended">
                  Next recommended step: <span className="text-[#9C1E22] font-medium">{w.next_recommended_step}</span>
                </div>
              )}
              {prog.categories && (
                <div className="grid grid-cols-3 gap-2 mt-4" data-testid="dashboard-progress-breakdown">
                  {[
                    { key: "checklist", label: "Checklist", icon: ListChecks },
                    { key: "documents", label: "Documents", icon: FileText },
                    { key: "meetings", label: "Meetings", icon: Video },
                  ].map(({ key, label, icon: Icon }) => {
                    const c = prog.categories[key] || { completed: 0, total: 0, percent: 0 };
                    return (
                      <div key={key} className="rounded-xl bg-[#F4F8FB] p-3" data-testid={`dashboard-progress-cat-${key}`}>
                        <div className="flex items-center gap-1.5 text-[12px] font-semibold text-[#1C4358]"><Icon size={13} /> {label}</div>
                        <div className="text-[13px] text-[#252221] mt-1 tabular-nums">
                          {c.total > 0 ? <><span className="font-semibold">{c.completed}</span> of {c.total}</> : <span className="text-muted-foreground">Nothing yet</span>}
                        </div>
                        <Progress value={c.percent} className="h-1.5 mt-1.5" />
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          )}

          {w?.is_primary && (
            <Card className="p-5 border-[#E6EDF2] bg-[#F4F8FB] flex flex-col" data-testid="dashboard-invite-family-nudge">
              <span className="h-10 w-10 rounded-xl bg-[#1C4358]/10 text-[#1C4358] flex items-center justify-center"><Users size={19} /></span>
              <div className="font-display font-semibold text-[15px] text-[#252221] mt-2.5">Bring family into the loop</div>
              <p className="text-[13px] text-muted-foreground mt-1 flex-1">
                Want another family member to stay in the loop? Invite them to view the parts of your family plan you choose.
              </p>
              <Button onClick={inviteFamily} className="bg-[#1C4358] hover:bg-[#163545] text-white gap-1.5 h-10 rounded-full mt-3 w-full sm:w-auto" data-testid="dashboard-invite-family-btn">
                <UserPlus size={15} /> Invite family member
              </Button>
            </Card>
          )}
        </div>
      )}

      {/* Quick facts */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Card className="p-4 border-[#E6EDF2]" data-testid="client-lane-card">
          <div className="text-[11px] uppercase tracking-wide text-[#1C4358]/60 font-bold flex items-center gap-1.5"><Map size={13} /> Your service lane</div>
          <div className="text-[16px] font-semibold text-[#252221] mt-1">{laneLabel(w?.service_lane)}</div>
        </Card>
        <Card className="p-4 border-[#E6EDF2]" data-testid="client-contact-card">
          <div className="text-[11px] uppercase tracking-wide text-[#1C4358]/60 font-bold flex items-center gap-1.5"><UserRound size={13} /> Your MovingMyMama guide</div>
          <div className="text-[16px] font-semibold text-[#252221] mt-1">{w?.contact_name || "Being assigned"}</div>
        </Card>
        <Card className="p-4 border-[#E6EDF2]" data-testid="client-docs-card">
          <div className="text-[11px] uppercase tracking-wide text-[#1C4358]/60 font-bold flex items-center gap-1.5"><FileText size={13} /> Documents requested</div>
          <div className="text-[16px] font-semibold text-[#252221] mt-1">{w?.documents_requested || 0} to upload</div>
        </Card>
      </div>

      {/* Navigation tiles */}
      <div>
        <div className="text-[13px] font-bold uppercase tracking-wide text-[#1C4358]/70 mb-2">Everything in one place</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Tile icon={Map} title="Your roadmap" desc={w?.has_roadmap ? "See your family transition plan" : "Coming soon from your guide"} to="/portal/roadmap" testid="client-tile-roadmap" />
          <Tile icon={ListChecks} title="Checklist" desc={w?.checklist_open ? `${w.checklist_open} step${w.checklist_open > 1 ? "s" : ""} to do` : "Your next steps, one at a time"} to="/portal/checklist" testid="client-tile-checklist" />
          <Tile icon={Video} title="Meetings" desc="Join meetings and see your recaps" to="/portal/meetings" testid="client-tile-meetings" />
          <Tile icon={FileText} title="Documents" desc="Upload and view your documents" to="/portal/documents" testid="client-tile-documents" />
          <Tile icon={MessagesSquare} title="Messages" desc="Talk to your guide anytime" to="/portal/messages" testid="client-tile-messages" />
        </div>
      </div>
    </div>
  );
}
